/**
 * JS port of backend/utils/variant_key.py :: normalize_variant_key.
 *
 * Used by the dispatch pop-up overlay (and future UIs) to recognise that
 * '500 gm', '500+ gm', '500gm', 'Pieces of 500+ gm', 'Packets of 500 gm'
 * and 'Pack of 500 gm' all refer to the same packaging size. The exact id
 * / exact-name match remains the first choice everywhere; this normalised
 * key is only ever a fallback.
 *
 * Keep this file in lockstep with backend/utils/variant_key.py. Both must
 * resolve the same cluster to the same key.
 */

const PREFIX_PATTERNS = [
  /^packets?\s+of\s+/,
  /^packet\s+/,
  /^pkts?\s+of\s+/,
  /^pkt\s+/,
  /^pieces?\s+of\s+/,
  /^pcs?\s+of\s+/,
  /^pack\s+of\s+/,
  /^pack\s+/,
  /^box\s+of\s+/,
  /^bag\s+of\s+/,
  /^bunch\s+of\s+/,
  /^carton\s+of\s+/,
];

const UNIT_SYNONYMS = [
  { group: ['grams', 'gram', 'gms', 'gm', 'g'], canonical: 'gm' },
  { group: ['kilograms', 'kilogram', 'kilos', 'kilo', 'kgs', 'kg'], canonical: 'kg' },
  { group: ['pieces', 'piece', 'pcs', 'pc'], canonical: 'pc' },
  { group: ['litres', 'litre', 'liters', 'liter', 'ltrs', 'ltr', 'lts', 'lt', 'l'], canonical: 'l' },
  { group: ['millilitres', 'millilitre', 'milliliters', 'milliliter', 'mls', 'ml'], canonical: 'ml' },
  { group: ['dozens', 'dozen', 'doz', 'dz'], canonical: 'dz' },
  { group: ['bunch', 'bunches'], canonical: 'bunch' },
  { group: ['packet', 'packets', 'pkt', 'pkts'], canonical: 'pkt' },
];

function stripPrefixes(s) {
  let changed = true;
  while (changed) {
    changed = false;
    for (const pat of PREFIX_PATTERNS) {
      const next = s.replace(pat, '');
      if (next !== s) {
        s = next;
        changed = true;
      }
    }
  }
  return s;
}

function canonicaliseUnits(s) {
  for (const { group, canonical } of UNIT_SYNONYMS) {
    // Longest synonym first so 'gms' is tried before 'gm'.
    const sorted = [...group].sort((a, b) => b.length - a.length);
    for (const syn of sorted) {
      if (syn === canonical) continue;
      // Word-boundary-ish: not preceded or followed by [a-z].
      const esc = syn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(?<![a-z])${esc}(?![a-z])`, 'g');
      s = s.replace(re, canonical);
    }
  }
  return s;
}

/**
 * Return a canonical match-key for a packaging / variant name.
 *
 *   normalizeVariantKey('500 gm')              -> '500gm'
 *   normalizeVariantKey('500+ gm')             -> '500gm'
 *   normalizeVariantKey('500gm')               -> '500gm'
 *   normalizeVariantKey('Pieces of 500+ gm')   -> '500gm'
 *   normalizeVariantKey('Packets of 500 gm')   -> '500gm'
 *   normalizeVariantKey('Pack of 1 Kg')        -> '1kg'
 *   normalizeVariantKey('250-260 gm')          -> '250-260gm'
 *   normalizeVariantKey('')                    -> ''
 *   normalizeVariantKey(null)                  -> ''
 */
export function normalizeVariantKey(name) {
  if (!name) return '';
  let s = String(name).trim().toLowerCase();
  // Collapse whitespace runs to a single space before anything else.
  s = s.replace(/\s+/g, ' ');
  // Drop any leading free-text prefix.
  s = stripPrefixes(s).trim();
  // Canonicalise unit tokens.
  s = canonicaliseUnits(s);
  // Drop '+' - '500gm' == '500+gm' per product owner call.
  s = s.replace(/\+/g, '');
  // Remove whitespace between a number and its unit, collapse residual spaces.
  s = s.replace(/(\d)\s+([a-z])/g, '$1$2');
  s = s.replace(/\s+/g, ' ').trim();
  // Final tidy: if the string starts with "<qty><unit>" (optionally a
  // range), trim any trailing descriptive text so '500 gm packet' and
  // '500 gm' land on the same key. If no clean match, keep the whole
  // string so unusual names don't accidentally collide.
  const m = s.match(/^(\d+(?:[-.]\d+)?)(gm|kg|ml|l|pc|dz|pkt|bunch)\b/);
  if (m) {
    s = m[1] + m[2];
  }
  return s;
}

export default normalizeVariantKey;
